import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { onColorLinksCss } from '../utils/onColorLinks';

// ============================================================================
// Centered closing CTA — bold headline, short supporting line, single CTA
// button. Full-bleed primary-color background with an optional angled photo
// behind it so the section slots into the Z Modular visual language used by
// FloorplansList / ConvenienceCallout / LifestyleFocus.
// ============================================================================

const backgroundDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Community exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a wide community-wide gallery image — exterior, aerial, courtyard, pool deck, or clubroom — that sells the overall sense of place. Skip bedrooms, bathrooms, closets, and floor-plan diagrams.',
    context:
      'Full-bleed background behind the closing lifestyle CTA, under a heavy primary-color overlay. Should feel broad and inviting, not a close-up of a single unit.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Label',
      type: 'text',
      defaultValue: 'Schedule a Tour',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function LifestyleClosingCTA() {
  useRevealStyles();

  const background = useSlotGroup('lifestyleclosingcta.background', backgroundDef);
  const cta = useSlotGroup('lifestyleclosingcta.cta', ctaDef);
  const contentRef = useScrollReveal<HTMLDivElement>();
  const ctaColorVars = useCtaColorVars();

  return (
    <section className="lcc-section" style={ctaColorVars}>
      {/* Background photo + 82% primary overlay. */}
      <div className="lcc-bg">
        <EditableSlotGroup
          groupPath="lifestyleclosingcta.background"
          definition={backgroundDef}
        >
          <div className="absolute inset-0">
            {background.image ? (
              <VibesitesImage
                url={background.image}
                alt={background.alt || ''}
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-primary)]" />
            )}
            <div className="absolute inset-0 bg-[var(--vibe-color-primary)] opacity-[0.85]" />
          </div>
        </EditableSlotGroup>
      </div>

      <div ref={contentRef} className="reveal-fade-up lcc-container">
        <RichTextContent
          path="lifestyleclosingcta.eyebrow"
          label="Eyebrow"
          defaultValue="<p>Make Your Move</p>"
          as="div"
          className="lcc-eyebrow"
        />
        <RichTextContent
          path="lifestyleclosingcta.heading"
          label="Heading"
          defaultValue='<p>Make Your Move to <span data-type="field-token" data-field="property_name"></span> Today</p>'
          as="h2"
          className="lcc-heading"
        />
        <RichTextContent
          path="lifestyleclosingcta.subheading"
          label="Subheading"
          defaultValue="<p>Tour the community, meet the team, and see which floor plan feels most like home.</p>"
          as="div"
          className="lcc-sub"
        />

        <EditableSlotGroup groupPath="lifestyleclosingcta.cta" definition={ctaDef}>
          <a
            href={resolveHref(cta.url || '/schedule-a-tour')}
            target={cta._target || '_self'}
            className="lcc-cta"
          >
            {cta.label || 'Schedule a Tour'}
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        ${onColorLinksCss('.lcc-section [data-vibesite-richtext]')}
        .lcc-section {
          position: relative;
          overflow: hidden;
          background: var(--vibe-color-primary);
          padding: 6rem 1.5rem 6rem;
        }
        .lcc-bg {
          position: absolute;
          inset: 0;
        }

        .lcc-container {
          position: relative;
          z-index: 1;
          max-width: 720px;
          margin: 0 auto;
          text-align: center;
          color: #ffffff;
        }

        .lcc-eyebrow,
        .lcc-eyebrow * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .lcc-eyebrow {
          color: rgba(255, 255, 255, 0.78);
          font-size: 0.78rem;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          margin: 0 0 1rem;
        }
        .lcc-eyebrow p { margin: 0; }

        .lcc-heading,
        .lcc-heading * {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
        }
        .lcc-heading {
          font-size: clamp(2.1rem, 5vw, 3.5rem);
          line-height: 1.05;
          letter-spacing: 0.03em;
          margin: 0 auto 1.25rem;
          max-width: 28rem;
        }

        .lcc-sub,
        .lcc-sub * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .lcc-sub {
          color: rgba(255, 255, 255, 0.88);
          font-size: 1rem;
          line-height: 1.7;
          max-width: 32rem;
          margin: 0 auto 2.5rem;
        }
        .lcc-sub p { margin: 0; }

        .lcc-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 1.1rem 2.75rem;
          background: var(--cta-bg, var(--vibe-color-secondary));
          color: var(--cta-text, var(--vibe-color-text-on-secondary, #ffffff));
          border: 1.5px solid var(--cta-bg, var(--vibe-color-secondary));
          font-family: var(--vibe-font-heading);
          font-size: 0.9rem;
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease,
                      transform 0.25s ease, border-color 0.25s ease;
        }
        .lcc-cta:hover {
          background: #ffffff;
          color: var(--cta-bg, var(--vibe-color-secondary));
          border-color: #ffffff;
          transform: translateY(-2px);
        }

        @media (max-width: 639px) {
          .lcc-section { padding: 4.5rem 1.25rem 4.5rem; }
          .lcc-cta { width: 100%; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleClosingCTA',
  displayName: 'Lifestyle Closing CTA',
  tokens: {
    'lifestyleclosingcta.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Make Your Move</p>',
    },
    'lifestyleclosingcta.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p>Make Your Move to <span data-type="field-token" data-field="property_name"></span> Today</p>',
    },
    'lifestyleclosingcta.subheading': {
      label: 'Subheading',
      type: 'richtext',
      defaultValue:
        '<p>Tour the community, meet the team, and see which floor plan feels most like home.</p>',
    },
  },
  slotGroups: {
    'lifestyleclosingcta.background': backgroundDef,
    'lifestyleclosingcta.cta': ctaDef,
  },
};

(LifestyleClosingCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default LifestyleClosingCTA;
